import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { locations, formatErr, vehicles as vApi } from "@/lib/api";
import { Plus, MapPin, Warning, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

export function LocationsList() {
  const [list, setList] = useState([]);
  const reload = () => locations.list().then(setList);
  useEffect(() => { reload(); }, []);
  const del = async (id) => {
    if (!window.confirm("Delete location?")) return;
    try { await locations.del(id); reload(); toast.success("Deleted"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return <AppShell>
    <div className="flex items-center justify-between mb-6">
      <h1 className="font-display font-black text-3xl md:text-4xl">Location Memory</h1>
      <Link to="/app/locations/new" data-testid="btn-new-location" className="btn-amber"><Plus size={16} weight="bold"/> Add location</Link>
    </div>
    <div className="grid md:grid-cols-2 gap-4">
      {list.length === 0 && <div className="col-span-full border-2 border-dashed p-10 text-center text-muted-foreground">No saved locations yet.</div>}
      {list.map(l => (
        <div key={l.id} data-testid={`loc-${l.id}`} className="bg-white border border-border/60 p-5 relative">
          {l.has_alert && <span className="absolute top-3 right-3 inline-flex items-center gap-1 eyebrow text-accent"><Warning size={14} weight="fill"/> Alert</span>}
          <div className="flex items-center gap-2">
            <MapPin size={22} className="text-primary"/>
            <h3 className="font-display font-bold text-lg">{l.name}</h3>
          </div>
          <div className="text-sm text-muted-foreground mt-1">{l.address}</div>
          {l.has_alert && <div className="mt-3 border-l-4 border-accent bg-accent/10 p-2 text-sm font-bold">"{l.alert_message}"</div>}
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            {l.access_notes && <N k="Access" v={l.access_notes}/>}
            {l.arrival_notes && <N k="Arrival" v={l.arrival_notes}/>}
            {l.loading_notes && <N k="Loading" v={l.loading_notes}/>}
            {l.gate_code && <N k="Gate code" v={l.gate_code}/>}
          </div>
          <button onClick={()=>del(l.id)} data-testid={`btn-del-${l.id}`} className="mt-3 text-xs text-destructive flex items-center gap-1 hover:underline"><Trash size={14}/> Delete</button>
        </div>
      ))}
    </div>
  </AppShell>;
}
function N({ k, v }) { return <div><div className="eyebrow text-muted-foreground">{k}</div><div>{v}</div></div>; }

export function NewLocation() {
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "", address: "", category: "pickup",
    access_notes: "", arrival_notes: "", loading_notes: "", general_notes: "",
    gate_code: "", has_alert: false, alert_message: ""
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target && 'target' in e ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    try { await locations.create(f); toast.success("Location saved"); nav("/app/locations"); }
    catch (err) { toast.error(formatErr(err)); }
  };
  return <AppShell>
    <Link to="/app/locations" className="eyebrow text-accent">← Back</Link>
    <h1 className="font-display font-black text-3xl md:text-4xl mb-6">Save Location</h1>
    <form onSubmit={submit} className="max-w-2xl bg-white border border-border/60 p-6 space-y-4">
      <F label="Name" tid="in-name" value={f.name} onChange={set("name")} required/>
      <F label="Address" tid="in-address" value={f.address} onChange={set("address")} required/>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" data-testid="in-has-alert" checked={f.has_alert} onChange={e=>setF({...f,has_alert:e.target.checked})}/>
        <span className="eyebrow">Make this an alert</span>
      </label>
      {f.has_alert && <F label="Alert message (shown when address matches on a future load)" tid="in-alert-msg" value={f.alert_message} onChange={set("alert_message")}/>}
      <F label="Access notes (turns, wires, routes)" tid="in-access" value={f.access_notes} onChange={set("access_notes")} textarea/>
      <F label="Arrival notes (gate, check-in, hours)" tid="in-arrival" value={f.arrival_notes} onChange={set("arrival_notes")} textarea/>
      <F label="Loading / unloading notes" tid="in-loading" value={f.loading_notes} onChange={set("loading_notes")} textarea/>
      <F label="Gate code (optional)" tid="in-gate" value={f.gate_code} onChange={set("gate_code")}/>
      <button data-testid="btn-save-location" className="btn-amber w-full">Save Location</button>
    </form>
  </AppShell>;
}

export function History() {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  useEffect(() => {
    let live = true;
    vApi.list({ q }).then(r => { if (live) setList(r); });
    return () => { live = false; };
  }, [q]);
  return <AppShell>
    <h1 className="font-display font-black text-3xl md:text-4xl mb-6">History & Search</h1>
    <div className="flex items-center gap-2 bg-white border-2 border-border focus-within:border-accent px-3 py-2 mb-5 max-w-2xl">
      <MagnifyingGlass size={18} className="text-muted-foreground"/>
      <input data-testid="in-search" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="VIN · make · model · address" className="flex-1 outline-none bg-transparent"/>
    </div>
    <div className="space-y-2">
      {list.length === 0 && <div className="border-2 border-dashed p-10 text-center text-muted-foreground">No records match that search.</div>}
      {list.map(v => (
        <Link key={v.id} to={`/app/vehicles/${v.id}`} data-testid={`hist-${v.id}`} className="block bg-white border border-border/60 p-4 hover:border-accent">
          <div className="font-mono-tech text-[11px] text-muted-foreground">{v.vin}</div>
          <div className="font-display font-bold">{v.year} {v.make} {v.model}</div>
          <div className="text-xs text-muted-foreground">{v.pickup_address || "—"} → {v.delivery_address || "—"} · {v.status.replace("_"," ")}</div>
        </Link>
      ))}
    </div>
  </AppShell>;
}

function F({ label, tid, value, onChange, textarea, required }) {
  return <label className="block">
    <span className="eyebrow block mb-1">{label}</span>
    {textarea
      ? <textarea data-testid={tid} value={value} onChange={onChange} rows={2} className="w-full border-2 border-border focus:border-accent px-3 py-2 outline-none"/>
      : <input data-testid={tid} value={value} onChange={onChange} required={required} className="w-full border-2 border-border focus:border-accent px-3 py-2 outline-none"/>}
  </label>;
}
